import {
  Attendee,
  AttendeeStatus,
  EventCoupon,
  EventOperationsState,
  EventSession,
  PaymentStatus,
  RegistrationFormField,
  TicketType,
} from "../types";

const revenueStatuses = new Set<PaymentStatus>(["paid", "partially_refunded"]);

export function getOrderNetRevenue(
  state: EventOperationsState,
  order: EventOperationsState["orders"][number],
): number {
  if (!revenueStatuses.has(order.status)) return 0;
  const refunded = state.payments
    .filter((payment) => payment.orderId === order.id)
    .reduce((sum, payment) => sum + Math.max(0, payment.refundAmount || 0), 0);
  return Math.max(0, order.total - refunded);
}

export interface EventOverviewMetrics {
  totalAttendees: number;
  registeredAttendees: number;
  confirmedRevenue: number;
  pendingRevenue: number;
  refundedRevenue: number;
  averageTicket: number;
  soldTickets: number;
  occupancyRate: number;
  checkoutConversion: number;
  checkInRate: number;
  noShowRate: number;
  scholarshipsUsed: number;
  scholarshipsPending: number;
  confirmedSpeakers: number;
  incompleteSpeakers: number;
  sessionsWithoutSpeakers: number;
  nextPriceChange?: string;
}

export function calculateEventOverview(
  state: EventOperationsState,
): EventOverviewMetrics {
  const registered = state.attendees.filter((attendee) =>
    ["registered", "checked_in", "no_show"].includes(
      attendee.registrationStatus,
    ),
  );
  const completedOrders = state.orders.filter((order) =>
    revenueStatuses.has(order.status),
  );
  const pendingOrders = state.orders.filter((order) =>
    ["pending", "processing"].includes(order.status),
  );
  const confirmedRevenue = completedOrders.reduce(
    (sum, order) => sum + getOrderNetRevenue(state, order),
    0,
  );
  const pendingRevenue = pendingOrders.reduce(
    (sum, order) => sum + order.total,
    0,
  );
  const refundedRevenue = state.payments.reduce(
    (sum, payment) => sum + payment.refundAmount,
    0,
  );
  const checkedInCount = state.attendees.filter(
    (attendee) => attendee.checkInStatus === "checked_in",
  ).length;
  const noShowCount = state.attendees.filter(
    (attendee) => attendee.registrationStatus === "no_show",
  ).length;
  const checkoutStarted = state.attendees.filter((attendee) =>
    ["payment_pending", "registered", "checked_in", "no_show"].includes(
      attendee.registrationStatus,
    ),
  ).length;
  const scholarshipsUsed = state.scholarships.filter(
    (item) => item.status === "used" || item.status === "code_issued",
  ).length;
  const upcomingTicketDates = state.ticketTypes
    .filter((ticket) => ticket.active && ticket.saleEndsAt)
    .map((ticket) => ticket.saleEndsAt)
    .filter((date) => new Date(`${date}T23:59:59`).getTime() >= Date.now())
    .sort();

  return {
    totalAttendees: state.attendees.length,
    registeredAttendees: registered.length,
    confirmedRevenue,
    pendingRevenue,
    refundedRevenue,
    averageTicket:
      completedOrders.length > 0
        ? confirmedRevenue / completedOrders.length
        : 0,
    soldTickets: completedOrders.length,
    occupancyRate:
      state.event.capacity > 0
        ? (registered.length / state.event.capacity) * 100
        : 0,
    checkoutConversion:
      checkoutStarted > 0
        ? (completedOrders.length / checkoutStarted) * 100
        : 0,
    checkInRate:
      registered.length > 0 ? (checkedInCount / registered.length) * 100 : 0,
    noShowRate:
      registered.length > 0 ? (noShowCount / registered.length) * 100 : 0,
    scholarshipsUsed,
    scholarshipsPending: state.scholarships.filter(
      (item) => item.status === "requested" || item.status === "under_review",
    ).length,
    confirmedSpeakers: state.speakers.filter((speaker) =>
      ["confirmed", "complete", "published"].includes(speaker.status),
    ).length,
    incompleteSpeakers: state.speakers.filter(
      (speaker) =>
        speaker.status !== "cancelled" && speaker.pendingAssets.length > 0,
    ).length,
    sessionsWithoutSpeakers: state.sessions.filter(
      (session) =>
        session.status !== "cancelled" && session.speakerIds.length === 0,
    ).length,
    nextPriceChange: upcomingTicketDates[0],
  };
}

export function normalizeEventOperationsState(
  value: unknown,
  fallback: EventOperationsState,
): EventOperationsState {
  const hasStoredState = Boolean(value && typeof value === "object");
  const candidate = hasStoredState
    ? (value as Partial<EventOperationsState>)
    : {};
  const arrayKeys: Array<
    keyof Pick<
      EventOperationsState,
      | "attendees"
      | "ticketTypes"
      | "orders"
      | "payments"
      | "scholarships"
      | "speakers"
      | "sessions"
      | "rooms"
      | "tracks"
      | "campaigns"
      | "checkIns"
      | "sessionRegistrations"
      | "communications"
      | "coupons"
      | "waitlist"
      | "activityLogs"
      | "registrationFields"
    >
  > = [
    "attendees",
    "ticketTypes",
    "orders",
    "payments",
    "scholarships",
    "speakers",
    "sessions",
    "rooms",
    "tracks",
    "campaigns",
    "checkIns",
    "sessionRegistrations",
    "communications",
    "coupons",
    "waitlist",
    "activityLogs",
    "registrationFields",
  ];

  const normalized = {
    ...fallback,
    ...candidate,
    event:
      candidate.event && typeof candidate.event === "object"
        ? { ...fallback.event, ...candidate.event }
        : { ...fallback.event },
  } as EventOperationsState;
  for (const key of arrayKeys) {
    const source = Array.isArray(candidate[key])
      ? candidate[key]
      : fallback[key];
    normalized[key] = [...source] as never;
  }
  normalized.version = 2;
  return normalized;
}

export function createEventEntityId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createAttendeeCode(
  firstName: string,
  lastName: string,
): string {
  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "AT";
  return `GR-${initials}-${Date.now().toString(36).toUpperCase()}`;
}

export function resolveTicketType(
  ticketTypes: TicketType[],
  selectedId: string,
  referenceDate = new Date(),
): TicketType | undefined {
  const visited = new Set<string>();
  let ticket = ticketTypes.find((item) => item.id === selectedId);
  const today = referenceDate.toISOString().slice(0, 10);
  while (ticket && !visited.has(ticket.id)) {
    visited.add(ticket.id);
    const soldOut = ticket.capacity > 0 && ticket.sold >= ticket.capacity;
    const notStarted = Boolean(
      ticket.saleStartsAt && ticket.saleStartsAt > today,
    );
    const ended = Boolean(ticket.saleEndsAt && ticket.saleEndsAt < today);
    if (ticket.active && !soldOut && !notStarted && !ended) return ticket;
    ticket = ticket.nextTicketTypeId
      ? ticketTypes.find((item) => item.id === ticket?.nextTicketTypeId)
      : undefined;
  }
  return undefined;
}

export function getVisibleRegistrationFields(
  fields: RegistrationFormField[],
  responses: Record<string, unknown>,
): RegistrationFormField[] {
  return fields
    .filter((field) => field.active !== false)
    .filter(
      (field) =>
        !field.conditionalOn ||
        String(responses[field.conditionalOn.fieldId] ?? "") ===
          field.conditionalOn.equals,
    )
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export interface CouponCalculation {
  valid: boolean;
  discount: number;
  message: string;
}

export interface CreateEventRegistrationInput {
  firstName: string;
  lastName: string;
  email: string;
  whatsapp?: string;
  country: string;
  profession: string;
  institution?: string;
  ticketTypeId: string;
  couponCode?: string;
  formResponses?: Record<string, string | boolean | string[]>;
  source?: string;
  campaignId?: string;
}

export interface CreateEventRegistrationResult {
  ok: boolean;
  state: EventOperationsState;
  attendee?: Attendee;
  message: string;
}

export function createEventRegistration(
  state: EventOperationsState,
  input: CreateEventRegistrationInput,
  referenceDate = new Date(),
): CreateEventRegistrationResult {
  if (!["published", "active"].includes(state.event.status)) {
    return {
      ok: false,
      state,
      message: "Las inscripciones no están abiertas para este evento.",
    };
  }
  const reservedSeats = state.ticketTypes.reduce(
    (sum, ticket) => sum + Math.max(0, ticket.sold),
    0,
  );
  if (state.event.capacity > 0 && reservedSeats >= state.event.capacity) {
    return {
      ok: false,
      state,
      message: "El evento alcanzó su capacidad total.",
    };
  }
  const email = input.email.trim().toLowerCase();
  if (
    !input.firstName.trim() ||
    !input.lastName.trim() ||
    !email ||
    !input.country.trim()
  ) {
    return {
      ok: false,
      state,
      message: "Completa nombre, apellido, email y país.",
    };
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    return { ok: false, state, message: "El email no es válido." };
  if (
    state.attendees.some((attendee) => attendee.email.toLowerCase() === email)
  ) {
    return {
      ok: false,
      state,
      message: "Ya existe una inscripción con ese email.",
    };
  }
  const ticket = resolveTicketType(
    state.ticketTypes,
    input.ticketTypeId,
    referenceDate,
  );
  if (!ticket)
    return {
      ok: false,
      state,
      message: "El ticket seleccionado ya no está disponible.",
    };
  const couponCode = input.couponCode?.trim().toUpperCase() || "";
  const coupon = couponCode
    ? state.coupons.find((item) => item.code.toUpperCase() === couponCode)
    : undefined;
  const accessCodeMatches = Boolean(
    couponCode && ticket.accessCode?.toUpperCase() === couponCode,
  );
  if (couponCode && !coupon && !accessCodeMatches)
    return { ok: false, state, message: "El código ingresado no existe." };
  const calculation = coupon
    ? calculateCouponDiscount(coupon, ticket, ticket.price, referenceDate)
    : undefined;
  if (coupon && !calculation?.valid)
    return {
      ok: false,
      state,
      message: calculation?.message || "El código no es válido.",
    };
  if (ticket.visibility === "hidden" && !coupon && !accessCodeMatches) {
    return {
      ok: false,
      state,
      message: "Este ticket requiere un código de acceso.",
    };
  }
  const discount = calculation?.discount || 0;
  const total = Math.max(0, ticket.price - discount);
  const now = referenceDate.toISOString();
  const attendeeId = createEventEntityId("att");
  const activityId = createEventEntityId("event-log");
  const requiresReview = ticket.requiresApproval;
  const attendee: Attendee = {
    id: attendeeId,
    eventId: state.event.id,
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email,
    whatsapp: input.whatsapp?.trim() || undefined,
    country: input.country.trim(),
    profession: input.profession.trim() || "Sin especificar",
    institution: input.institution?.trim() || undefined,
    ticketTypeId: ticket.id,
    price: ticket.price,
    currency: ticket.currency,
    paymentStatus: total > 0 ? "pending" : "paid",
    registrationStatus: requiresReview
      ? "under_review"
      : total > 0
        ? "payment_pending"
        : "registered",
    attribution: {
      source: input.source || "Formulario de inscripción",
      campaignId: input.campaignId,
      code: couponCode || undefined,
    },
    registeredAt: now,
    uniqueCode: createAttendeeCode(input.firstName, input.lastName),
    qrValue: `grosym:${state.event.id}:${attendeeId}`,
    checkInStatus: "not_checked_in",
    tags: ["registro-web"],
    activityLogIds: [activityId],
    formResponses: input.formResponses,
  };
  const order = {
    id: createEventEntityId("order"),
    eventId: state.event.id,
    attendeeId,
    ticketTypeId: ticket.id,
    subtotal: ticket.price,
    discount,
    taxes: 0,
    total,
    currency: ticket.currency,
    status: total > 0 ? ("pending" as const) : ("paid" as const),
    createdAt: now,
    couponCode: couponCode || undefined,
  };
  const nextState: EventOperationsState = {
    ...state,
    attendees: [attendee, ...state.attendees],
    orders: [order, ...state.orders],
    ticketTypes: state.ticketTypes.map((item) =>
      item.id === ticket.id ? { ...item, sold: item.sold + 1 } : item,
    ),
    coupons: coupon
      ? state.coupons.map((item) =>
          item.id === coupon.id ? { ...item, used: item.used + 1 } : item,
        )
      : state.coupons,
    activityLogs: [
      {
        id: activityId,
        eventId: state.event.id,
        entityType: "attendee",
        entityId: attendeeId,
        action: "registration_created",
        description: requiresReview
          ? "Inscripción recibida y enviada a revisión."
          : total > 0
            ? "Inscripción recibida; pago pendiente."
            : "Inscripción confirmada.",
        createdAt: now,
      },
      ...state.activityLogs,
    ],
    updatedAt: now,
  };
  return {
    ok: true,
    state: nextState,
    attendee,
    message: requiresReview
      ? "Solicitud recibida. Quedó en revisión."
      : total > 0
        ? "Inscripción creada. El pago está pendiente."
        : "Inscripción confirmada.",
  };
}

export function calculateCouponDiscount(
  coupon: EventCoupon,
  ticket: TicketType,
  subtotal: number,
  referenceDate = new Date(),
): CouponCalculation {
  const today = referenceDate.toISOString().slice(0, 10);
  if (!coupon.active)
    return { valid: false, discount: 0, message: "El código está inactivo." };
  if (coupon.startsAt && coupon.startsAt > today)
    return {
      valid: false,
      discount: 0,
      message: "El código todavía no comenzó.",
    };
  if (coupon.endsAt && coupon.endsAt < today)
    return { valid: false, discount: 0, message: "El código venció." };
  if (coupon.available > 0 && coupon.used >= coupon.available)
    return {
      valid: false,
      discount: 0,
      message: "El código alcanzó su límite.",
    };
  if (coupon.ticketTypeId && coupon.ticketTypeId !== ticket.id)
    return {
      valid: false,
      discount: 0,
      message: "El código no aplica a este ticket.",
    };
  if (coupon.type === "hidden_ticket_access")
    return { valid: true, discount: 0, message: "Acceso habilitado." };
  if (coupon.type === "free_ticket")
    return {
      valid: true,
      discount: Math.max(0, subtotal),
      message: "Ticket gratuito aplicado.",
    };
  if (coupon.type === "percentage")
    return {
      valid: true,
      discount: Math.min(
        subtotal,
        (subtotal * Math.max(0, coupon.value)) / 100,
      ),
      message: "Descuento aplicado.",
    };
  return {
    valid: true,
    discount: Math.min(subtotal, Math.max(0, coupon.value)),
    message: "Descuento aplicado.",
  };
}

export interface SponsorEventImpact {
  sponsorId: string;
  attendees: number;
  leads: number;
  scholarshipsFunded: number;
  scholarshipsUsed: number;
  sponsoredSessions: number;
  checkIns: number;
  conversions: number;
  revenue: number;
}

export function calculateSponsorEventImpact(
  state: EventOperationsState,
  sponsorId: string,
): SponsorEventImpact {
  const scholarshipAttendeeIds = new Set(
    state.scholarships
      .filter((item) => item.sponsorId === sponsorId)
      .map((item) => item.attendeeId),
  );
  const attributed = state.attendees.filter(
    (item) =>
      item.attribution.sponsorId === sponsorId ||
      scholarshipAttendeeIds.has(item.id),
  );
  const attendeeIds = new Set(attributed.map((item) => item.id));
  const paidOrders = state.orders.filter(
    (item) =>
      attendeeIds.has(item.attendeeId) && revenueStatuses.has(item.status),
  );
  return {
    sponsorId,
    attendees: attendeeIds.size,
    leads: attributed.length,
    scholarshipsFunded: state.scholarships.filter(
      (item) => item.sponsorId === sponsorId,
    ).length,
    scholarshipsUsed: state.scholarships.filter(
      (item) =>
        item.sponsorId === sponsorId &&
        ["code_issued", "used"].includes(item.status),
    ).length,
    sponsoredSessions: state.sessions.filter(
      (item) => item.sponsorId === sponsorId,
    ).length,
    checkIns: state.checkIns.filter((item) => attendeeIds.has(item.attendeeId))
      .length,
    conversions: paidOrders.length,
    revenue: paidOrders.reduce(
      (sum, item) => sum + getOrderNetRevenue(state, item),
      0,
    ),
  };
}

export interface EventFinancialBreakdownRow {
  id: string;
  label: string;
  orders: number;
  revenue: number;
}

export function calculateEventFinancialBreakdowns(state: EventOperationsState) {
  const paidOrders = state.orders.filter((order) =>
    revenueStatuses.has(order.status),
  );
  const group = (
    keyFor: (
      attendee: Attendee,
      ticket?: TicketType,
    ) => { id: string; label: string },
  ) => {
    const rows = new Map<string, EventFinancialBreakdownRow>();
    for (const order of paidOrders) {
      const attendee = state.attendees.find(
        (item) => item.id === order.attendeeId,
      );
      if (!attendee) continue;
      const ticket = state.ticketTypes.find(
        (item) => item.id === order.ticketTypeId,
      );
      const key = keyFor(attendee, ticket);
      const current = rows.get(key.id) || { ...key, orders: 0, revenue: 0 };
      current.orders += 1;
      current.revenue += getOrderNetRevenue(state, order);
      rows.set(key.id, current);
    }
    return Array.from(rows.values()).sort((a, b) => b.revenue - a.revenue);
  };
  return {
    byTicket: group((_attendee, ticket) => ({
      id: ticket?.id || "none",
      label: ticket?.name || "Sin ticket",
    })),
    bySource: group((attendee) => ({
      id: attendee.attribution.source || "direct",
      label: attendee.attribution.source || "Directo",
    })),
    byCampaign: group((attendee) => {
      const campaign = state.campaigns.find(
        (item) => item.id === attendee.attribution.campaignId,
      );
      return {
        id: campaign?.id || "none",
        label: campaign?.name || "Sin campaña",
      };
    }),
  };
}

type ImportField =
  | "firstName"
  | "lastName"
  | "email"
  | "whatsapp"
  | "country"
  | "profession"
  | "institution"
  | "specialty"
  | "ticket"
  | "payment"
  | "registration"
  | "source"
  | "campaign"
  | "code"
  | "tags"
  | "notes"
  | "registeredAt";

export const EVENT_ATTENDEE_REQUIRED_HEADERS = [
  "Nombre",
  "Apellido",
  "Email",
  "País",
];
export const EVENT_ATTENDEE_OPTIONAL_HEADERS = [
  "WhatsApp",
  "Profesión",
  "Institución",
  "Especialidad",
  "Ticket",
  "Pago",
  "Inscripción",
  "Fuente",
  "Campaña",
  "Código",
  "Tags",
  "Notas",
  "Fecha",
];
export const EVENT_ATTENDEE_TEMPLATE_HEADERS = [
  ...EVENT_ATTENDEE_REQUIRED_HEADERS,
  ...EVENT_ATTENDEE_OPTIONAL_HEADERS,
];

const importAliases: Record<ImportField, string[]> = {
  firstName: ["nombre", "first name"],
  lastName: ["apellido", "last name"],
  email: ["email", "correo"],
  whatsapp: ["whatsapp", "telefono", "phone"],
  country: ["pais", "country"],
  profession: ["profesion", "profession"],
  institution: ["institucion", "empresa", "institution"],
  specialty: ["especialidad", "specialty"],
  ticket: ["ticket", "entrada", "tipo de entrada"],
  payment: ["pago", "estado de pago"],
  registration: ["inscripcion", "estado de inscripcion"],
  source: ["fuente", "source"],
  campaign: ["campana", "campaign"],
  code: ["codigo", "code", "cupon"],
  tags: ["tags", "etiquetas"],
  notes: ["notas", "notes"],
  registeredAt: ["fecha", "fecha de inscripcion"],
};

const normalizeImportText = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("es")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const importCell = (value: unknown) =>
  value instanceof Date
    ? value.toISOString().slice(0, 10)
    : String(value ?? "").trim();

const paymentImportMap: Record<string, PaymentStatus> = {
  pendiente: "pending",
  pending: "pending",
  procesando: "processing",
  processing: "processing",
  pagado: "paid",
  paid: "paid",
  fallido: "failed",
  failed: "failed",
  "reembolso parcial": "partially_refunded",
  "partially refunded": "partially_refunded",
  reembolsado: "refunded",
  refunded: "refunded",
  cancelado: "cancelled",
  cancelled: "cancelled",
};
const attendeeImportMap: Record<string, AttendeeStatus> = {
  invitado: "invited",
  invited: "invited",
  "inicio inscripcion": "started",
  started: "started",
  pendiente: "pending",
  pending: "pending",
  "en revision": "under_review",
  "under review": "under_review",
  aprobado: "approved",
  approved: "approved",
  "pago pendiente": "payment_pending",
  "payment pending": "payment_pending",
  inscripto: "registered",
  registered: "registered",
  "checked in": "checked_in",
  "checked-in": "checked_in",
  "no show": "no_show",
  cancelado: "cancelled",
  cancelled: "cancelled",
};

export interface EventAttendeeImportError {
  row: number;
  message: string;
}
export interface EventAttendeeImportResult {
  attendees: Attendee[];
  errors: EventAttendeeImportError[];
  missingRequiredHeaders: string[];
}

export function parseEventAttendeeRows(
  rowsSource: unknown[][],
  state: EventOperationsState,
): EventAttendeeImportResult {
  const rows = rowsSource.filter((row) =>
    row.some((cell) => importCell(cell) !== ""),
  );
  if (rows.length === 0)
    return {
      attendees: [],
      errors: [{ row: 1, message: "El archivo está vacío." }],
      missingRequiredHeaders: EVENT_ATTENDEE_REQUIRED_HEADERS,
    };
  const headers = rows[0].map(normalizeImportText);
  const indexes = Object.fromEntries(
    (Object.keys(importAliases) as ImportField[]).map((field) => [
      field,
      headers.findIndex((header) =>
        importAliases[field].some(
          (alias) => normalizeImportText(alias) === header,
        ),
      ),
    ]),
  ) as Record<ImportField, number>;
  const required: Array<[ImportField, string]> = [
    ["firstName", "Nombre"],
    ["lastName", "Apellido"],
    ["email", "Email"],
    ["country", "País"],
  ];
  const missingRequiredHeaders = required
    .filter(([field]) => indexes[field] < 0)
    .map(([, label]) => label);
  if (missingRequiredHeaders.length > 0)
    return { attendees: [], errors: [], missingRequiredHeaders };

  const errors: EventAttendeeImportError[] = [];
  const attendees: Attendee[] = [];
  const emails = new Set(
    state.attendees.map((item) => item.email.toLowerCase()),
  );
  const importedByTicket = new Map<string, number>();
  const reservedSeats = state.ticketTypes.reduce(
    (sum, ticket) => sum + Math.max(0, ticket.sold),
    0,
  );
  let importedSeats = 0;
  const get = (row: unknown[], field: ImportField) =>
    indexes[field] >= 0 ? importCell(row[indexes[field]]) : "";
  rows.slice(1).forEach((row, index) => {
    const rowNumber = index + 2;
    const firstName = get(row, "firstName");
    const lastName = get(row, "lastName");
    const email = get(row, "email").toLowerCase();
    const country = get(row, "country");
    const rowErrors: string[] = [];
    if (!firstName) rowErrors.push("falta Nombre");
    if (!lastName) rowErrors.push("falta Apellido");
    if (!email) rowErrors.push("falta Email");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      rowErrors.push("Email inválido");
    if (emails.has(email)) rowErrors.push("Email duplicado");
    if (!country) rowErrors.push("falta País");
    const ticketText = get(row, "ticket");
    const selectedTicket = ticketText
      ? state.ticketTypes.find(
          (item) =>
            normalizeImportText(item.id) === normalizeImportText(ticketText) ||
            normalizeImportText(item.name) === normalizeImportText(ticketText),
        )
      : undefined;
    if (ticketText && !selectedTicket) rowErrors.push("Ticket desconocido");
    if (
      selectedTicket?.capacity &&
      selectedTicket.sold + (importedByTicket.get(selectedTicket.id) || 0) >=
        selectedTicket.capacity
    )
      rowErrors.push("Ticket sin cupo disponible");
    if (
      selectedTicket &&
      state.event.capacity > 0 &&
      reservedSeats + importedSeats >= state.event.capacity
    )
      rowErrors.push("Evento sin cupo disponible");
    const paymentText = normalizeImportText(get(row, "payment"));
    const paymentStatus = paymentText
      ? paymentImportMap[paymentText]
      : selectedTicket?.price
        ? "pending"
        : "paid";
    if (paymentText && !paymentImportMap[paymentText])
      rowErrors.push("Estado de pago inválido");
    const registrationText = normalizeImportText(get(row, "registration"));
    const registrationStatus = registrationText
      ? attendeeImportMap[registrationText]
      : selectedTicket?.price
        ? "payment_pending"
        : "registered";
    if (registrationText && !attendeeImportMap[registrationText])
      rowErrors.push("Estado de inscripción inválido");
    if (rowErrors.length > 0) {
      errors.push({ row: rowNumber, message: rowErrors.join("; ") });
      return;
    }
    emails.add(email);
    if (selectedTicket) {
      importedByTicket.set(
        selectedTicket.id,
        (importedByTicket.get(selectedTicket.id) || 0) + 1,
      );
      importedSeats += 1;
    }
    const id = createEventEntityId(`att-import-${rowNumber}`);
    const campaignText = get(row, "campaign");
    const campaign = state.campaigns.find(
      (item) =>
        normalizeImportText(item.id) === normalizeImportText(campaignText) ||
        normalizeImportText(item.name) === normalizeImportText(campaignText),
    );
    const uniqueCode =
      get(row, "code") || createAttendeeCode(firstName, lastName);
    attendees.push({
      id,
      eventId: state.event.id,
      firstName,
      lastName,
      email,
      whatsapp: get(row, "whatsapp") || undefined,
      country,
      profession: get(row, "profession") || "Sin especificar",
      institution: get(row, "institution") || undefined,
      specialty: get(row, "specialty") || undefined,
      ticketTypeId: selectedTicket?.id,
      price: selectedTicket?.price || 0,
      currency: selectedTicket?.currency || state.event.defaultCurrency,
      paymentStatus,
      registrationStatus,
      attribution: {
        source: get(row, "source") || "Importación CSV/Excel",
        campaignId: campaign?.id,
        code: get(row, "code") || undefined,
      },
      registeredAt: get(row, "registeredAt") || new Date().toISOString(),
      uniqueCode,
      qrValue: `grosym:${state.event.id}:${id}`,
      checkInStatus:
        registrationStatus === "checked_in" ? "checked_in" : "not_checked_in",
      tags: get(row, "tags")
        .split(/[,;|]/)
        .map((item) => item.trim())
        .filter(Boolean),
      notes: get(row, "notes") || undefined,
      activityLogIds: [],
    });
  });
  return { attendees, errors, missingRequiredHeaders: [] };
}

export function mergeImportedAttendees(
  state: EventOperationsState,
  attendees: Attendee[],
): EventOperationsState {
  const now = new Date().toISOString();
  const orders = [...state.orders];
  const activityLogs = [...state.activityLogs];
  const soldByTicket = new Map<string, number>();
  const importedAttendees = attendees.map((attendee) => {
    const logId = createEventEntityId("event-log");
    activityLogs.unshift({
      id: logId,
      eventId: state.event.id,
      entityType: "attendee",
      entityId: attendee.id,
      action: "imported",
      description: "Inscripto importado desde CSV/Excel.",
      createdAt: now,
    });
    if (attendee.ticketTypeId) {
      soldByTicket.set(
        attendee.ticketTypeId,
        (soldByTicket.get(attendee.ticketTypeId) || 0) + 1,
      );
      orders.unshift({
        id: createEventEntityId("order"),
        eventId: state.event.id,
        attendeeId: attendee.id,
        ticketTypeId: attendee.ticketTypeId,
        subtotal: attendee.price,
        discount: 0,
        taxes: 0,
        total: attendee.price,
        currency: attendee.currency,
        status: attendee.paymentStatus,
        createdAt: attendee.registeredAt,
      });
    }
    return { ...attendee, activityLogIds: [logId] };
  });
  return {
    ...state,
    attendees: [...importedAttendees, ...state.attendees],
    orders,
    ticketTypes: state.ticketTypes.map((ticket) => ({
      ...ticket,
      sold: ticket.sold + (soldByTicket.get(ticket.id) || 0),
    })),
    activityLogs,
    updatedAt: now,
  };
}

export interface CheckInResult {
  state: EventOperationsState;
  ok: boolean;
  message: string;
  attendee?: Attendee;
}

export function registerEventCheckIn(
  state: EventOperationsState,
  code: string,
  operatorId: string,
  session?: EventSession,
): CheckInResult {
  const normalized = code.trim().toLowerCase();
  const attendee = state.attendees.find(
    (item) =>
      item.uniqueCode.toLowerCase() === normalized ||
      item.qrValue.toLowerCase() === normalized,
  );
  if (!attendee)
    return {
      state,
      ok: false,
      message: "No encontramos un inscripto con ese código.",
    };
  if (!["registered", "checked_in"].includes(attendee.registrationStatus))
    return {
      state,
      ok: false,
      message: "La inscripción todavía no está confirmada.",
      attendee,
    };
  const duplicate = state.checkIns.some(
    (item) =>
      item.attendeeId === attendee.id &&
      (item.sessionId || "") === (session?.id || ""),
  );
  if (duplicate)
    return {
      state,
      ok: false,
      message: session
        ? "El inscripto ya ingresó a esta sesión."
        : "El inscripto ya realizó el check-in general.",
      attendee,
    };
  const now = new Date().toISOString();
  const checkInId = createEventEntityId("checkin");
  const logId = createEventEntityId("event-log");
  return {
    ok: true,
    message: session
      ? `Check-in registrado en ${session.title}.`
      : "Check-in general registrado.",
    attendee,
    state: {
      ...state,
      attendees: state.attendees.map((item) =>
        item.id === attendee.id
          ? {
              ...item,
              ...(session
                ? {}
                : {
                    checkInStatus: "checked_in" as const,
                    registrationStatus: "checked_in" as const,
                  }),
              activityLogIds: [logId, ...item.activityLogIds],
            }
          : item,
      ),
      checkIns: [
        {
          id: checkInId,
          eventId: state.event.id,
          attendeeId: attendee.id,
          sessionId: session?.id,
          code: attendee.uniqueCode,
          checkedInAt: now,
          operatorId,
        },
        ...state.checkIns,
      ],
      activityLogs: [
        {
          id: logId,
          eventId: state.event.id,
          entityType: "attendee",
          entityId: attendee.id,
          action: session ? "session_check_in" : "checked_in",
          description: session
            ? `Check-in en ${session.title}.`
            : "Check-in general registrado.",
          actorId: operatorId,
          createdAt: now,
        },
        ...state.activityLogs,
      ],
      updatedAt: now,
    },
  };
}
